import { memo, useEffect, useRef } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { PAGE_ROUTES_PRIVATE, PAGE_ROUTES_PUBLIC } from '@appTypes/enums/pages'
import { initializeSession } from '@store/features/auth/auth.actions'
import { setSessionAnonymous } from '@store/features/auth/auth.slice'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { hasStoredSession } from '@utils/auth-session'
import { ContentLoader } from '@layout/Private/styled'

const SessionLoader = () => (
  <ContentLoader sx={{ position: 'fixed', minHeight: '100vh' }} />
)

/** Boots stored session once, then renders child routes. */
export const AuthGate = memo(() => {
  const dispatch = useAppDispatch()
  const sessionStatus = useAppSelector((state) => state.auth.sessionStatus)
  const bootstrapStarted = useRef(false)

  useEffect(() => {
    if (sessionStatus !== 'unknown' || bootstrapStarted.current) {
      return
    }

    bootstrapStarted.current = true

    if (!hasStoredSession()) {
      dispatch(setSessionAnonymous())
      return
    }

    dispatch(initializeSession())
  }, [dispatch, sessionStatus])

  if (sessionStatus === 'unknown' && hasStoredSession()) {
    return <SessionLoader />
  }

  return <Outlet />
})

/** Private routes — redirect guests to sign-in. */
export const RequireAuth = memo(() => {
  const sessionStatus = useAppSelector((state) => state.auth.sessionStatus)

  if (sessionStatus === 'unknown') {
    return <SessionLoader />
  }

  if (sessionStatus === 'anonymous') {
    return <Navigate to={PAGE_ROUTES_PUBLIC.SIGN_IN} replace />
  }

  return <Outlet />
})

/** Auth pages — redirect signed-in users to home. */
export const RequireGuest = memo(() => {
  const sessionStatus = useAppSelector((state) => state.auth.sessionStatus)

  if (sessionStatus === 'unknown') {
    return <SessionLoader />
  }

  if (sessionStatus === 'authenticated') {
    return <Navigate to={PAGE_ROUTES_PRIVATE.HOME} replace />
  }

  return <Outlet />
})

export default AuthGate
